// src/js/core/api.js
//
// REST呼び出しの唯一の入口（32_REST_API設計.md 第7章「クライアントSDK」）。
// コンポーネント側は fetch() を直接呼ばず、必ず api.get()/api.post() を経由する。
//
// 要件（32文書 7章の表）:
//   - リトライ: ネットワークエラー・429・5xxのみ指数バックオフで最大2回
//   - タイムアウト: AbortController、既定10秒（書き込み系は15秒）
//   - キャンセル: 呼び出し側からAbortSignalを渡せる
//   - 重複排除: 同一URL+同一パラメータのGETはin-flightのPromiseを共有する
//   - オフライン検知: navigator.onLine + online/offlineイベント
//   - エラー: XteApiError に統一（code/status/retryAfter/data）
//   - nonce自動更新: 403 xte_invalid_nonce を受けたら GET /config を1回だけ再取得し、
//     同一リクエストを1回だけ再試行する（32文書 3.3節）
//   - identity自動付与: 書き込み系は core/identity.js（別担当）からトークンを取得し
//     Authorization ヘッダへ自動付与。未発行なら発行してから送る
//
// identity.js の実際のインターフェース（不具合修正時に確認・統一。コメントを実装に合わせて修正）:
//   getIdentity(): string|null   トークン文字列を同期的に返す（未発行なら null）
//   ensureIdentity(): Promise<string|null> 書き込み操作の直前に呼ぶ。トークンを返す
//
// 旧コメントは `{ token, issued }` というオブジェクトを返す想定で書かれており、
// 本ファイルの rawRequest() もそれに合わせて `const { token } = getIdentity();` と
// 分割代入していたが、identity.js の実装は一貫して文字列を返す
// （`src/js/components/xte-bottom-nav.js` も `const token = getIdentity();` と
// 文字列として使っており、こちらが実装・呼び出し側双方の実態と一致する）。
// よってオブジェクト側ではなく文字列を返す実装に本ファイルを合わせた
// （docs/HANDOFF.md「ボトムナビ担当 → ビルド設定/フロントエンド基盤担当」参照）。
import { getIdentity, ensureIdentity } from './identity.js';

const API_BASE = '/wp-json/xte/v1';
const DEFAULT_TIMEOUT_MS = 10000;
const WRITE_TIMEOUT_MS = 15000;
const MAX_RETRIES = 2;
const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504]);

/** APIエラーを表す統一クラス。呼び出し側は instanceof XteApiError で判定する。 */
export class XteApiError extends Error {
  constructor({ code, message, status, retryAfter, data }) {
    super(message);
    this.name = 'XteApiError';
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter ?? null;
    this.data = data ?? null;
  }

  get isRateLimited() {
    return this.status === 429;
  }

  get isNetworkError() {
    return this.code === 'xte_network_error';
  }

  get isOffline() {
    return this.code === 'xte_offline';
  }

  get isTimeout() {
    return this.code === 'xte_timeout';
  }
}

// --- モジュール内で共有する状態: nonce・in-flight de-dupキャッシュ ---
let cachedNonce = null;
let cachedSettingsVersion = null;
let configPromise = null;
const inFlight = new Map(); // key: "GET url" -> Promise

function isOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function buildUrl(path, params = {}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(API_BASE + path, origin);
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * GET /config を叩いてnonce・settings_versionをリフレッシュする。
 * 同時に複数箇所から呼ばれても1回だけ実行する（32文書 3.3節）。
 */
async function refreshConfig() {
  if (!configPromise) {
    configPromise = rawRequest('GET', '/config', {}, { skipNonceRefreshOnFail: true })
      .then((data) => {
        if (data) {
          cachedNonce = data.rest_nonce ?? cachedNonce;
          cachedSettingsVersion = data.settings_version ?? cachedSettingsVersion;
        }
        return data;
      })
      .finally(() => {
        configPromise = null;
      });
  }
  return configPromise;
}

/** タイムアウト付き fetch。外部から渡された signal と内部タイムアウト signal を合成する。 */
async function fetchWithTimeout(url, init, timeoutMs, externalSignal) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);

  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * 低レベルのリクエスト実行。リトライ・エラー正規化のみを担当し、
 * de-dup・identity付与は上位関数（api.get/api.post）が行う。
 */
async function rawRequest(method, path, { params, body, signal } = {}, { skipNonceRefreshOnFail = false } = {}) {
  const isWrite = method !== 'GET';
  const url = buildUrl(path, method === 'GET' ? params : {});
  const timeoutMs = isWrite ? WRITE_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;

  if (!isOnline()) {
    throw new XteApiError({ code: 'xte_offline', message: 'オフラインです。接続を確認してください。', status: 0 });
  }

  const headers = { Accept: 'application/json' };

  // 識別子トークンはGET/書き込み系の両方に付与する。GET /notifications/unread-count
  // のような「自分の状態」を返すエンドポイントは、GETに一切トークンが付与されない
  // 従来実装だと常に匿名扱いになり未読バッジ等が機能しなかった
  // （docs/HANDOFF.md「ボトムナビ担当 → ビルド設定/フロントエンド基盤担当」参照）。
  // ただし GET は ensureIdentity() を呼ばない。31_匿名識別とセッション.md §1.2の
  // 「新規発行は書き込み操作の直前にのみ行う」方針により、閲覧のみのユーザーに
  // 無条件で識別子を新規発行しないため、既に発行済みのトークンがあれば使うだけに留める。
  const token = getIdentity();
  if (token) headers.Authorization = `Bearer ${token}`;

  if (isWrite) {
    headers['Content-Type'] = 'application/json';
    headers['X-WP-Nonce'] = cachedNonce ?? '';
  }

  let attempt = 0;
  let lastError;

  while (attempt <= MAX_RETRIES) {
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          credentials: 'same-origin',
        },
        timeoutMs,
        signal
      );

      if (response.status === 304) {
        return null; // 呼び出し側はキャッシュ済みデータを使い続ける
      }

      let json = null;
      try {
        json = await response.json();
      } catch {
        // 本文なし・不正JSON
      }

      if (!response.ok) {
        const apiError = new XteApiError({
          code: json?.code ?? 'xte_unknown_error',
          message: json?.message ?? `リクエストに失敗しました（${response.status}）`,
          status: response.status,
          retryAfter: Number(response.headers.get('Retry-After')) || json?.data?.retry_after || null,
          data: json?.data ?? null,
        });

        // nonce失効: 1回だけ自動更新して再試行する（skipNonceRefreshOnFailは
        // /config 自身の呼び出しでの無限ループを防ぐためのフラグ）。
        // rest_cookie_invalid_nonce はWPコアのCookie認証チェックが返すコード。
        // WPログイン中のブラウザでnonceのセッション不整合が起きた場合
        // （サーバ側は class-xte-rest-config.php の
        // create_rest_nonce_for_actual_visitor() で恒久修正済み）でも、
        // /config を取り直せば正しいセッションのnonceが得られて自己回復する。
        const isNonceError =
          apiError.code === 'xte_invalid_nonce' || apiError.code === 'rest_cookie_invalid_nonce';
        if (isNonceError && isWrite && !skipNonceRefreshOnFail && attempt === 0) {
          await refreshConfig();
          headers['X-WP-Nonce'] = cachedNonce ?? '';
          attempt += 1;
          continue;
        }

        if (RETRYABLE_STATUS.has(response.status) && attempt < MAX_RETRIES) {
          const backoffMs = apiError.retryAfter ? apiError.retryAfter * 1000 : 300 * 2 ** attempt;
          await sleep(Math.min(backoffMs, 8000));
          attempt += 1;
          lastError = apiError;
          continue;
        }

        throw apiError;
      }

      return json;
    } catch (err) {
      if (err instanceof XteApiError) throw err;

      if (err.name === 'AbortError' || err.name === 'TimeoutError') {
        if (signal?.aborted) return null; // 呼び出し側の意図的なキャンセルはエラー扱いにしない
        lastError = new XteApiError({ code: 'xte_timeout', message: 'タイムアウトしました。', status: 0 });
      } else {
        lastError = new XteApiError({ code: 'xte_network_error', message: '通信エラーが発生しました。', status: 0 });
      }

      if (attempt < MAX_RETRIES) {
        await sleep(300 * 2 ** attempt);
        attempt += 1;
        continue;
      }
      throw lastError;
    }
  }

  throw lastError;
}

/** GET専用: 同一URL+同一パラメータの同時リクエストを1本のPromiseにまとめる（重複排除）。 */
function dedupedGet(path, params, signal) {
  const key = buildUrl(path, params);
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = rawRequest('GET', path, { params, signal }).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

// --- 公開API ---

export const api = {
  /**
   * @param {string} path 例: '/videos'
   * @param {Record<string, string|number|undefined|null>} [params]
   * @param {AbortSignal} [signal]
   */
  get(path, params = {}, signal) {
    return dedupedGet(path, params, signal);
  },

  /**
   * 書き込み系。識別トークンが未発行なら発行してから送信する（32文書 3.2節・00文書 5.2節:
   * 発行は「初回に書き込み操作が必要になった瞬間」に遅延実行する）。
   * @param {string} path @param {object} [body] @param {AbortSignal} [signal]
   */
  async post(path, body = {}, signal) {
    await ensureIdentity();
    if (!cachedNonce) {
      await refreshConfig();
    }
    return rawRequest('POST', path, { body, signal });
  },

  /**
   * カーソルページングのヘルパ。next_cursor を追いながら呼び出し側にページ単位で
   * 結果を渡す非同期イテレータ（32文書 2.2節のカーソル方式に対応）。
   *
   * 使用例:
   *   for await (const page of api.paginate('/videos', { sort: 'hotness' })) { ... }
   *
   * @param {string} path @param {object} [params] @param {AbortSignal} [signal]
   */
  async *paginate(path, params = {}, signal) {
    let cursor;
    do {
      const page = await this.get(path, { ...params, cursor }, signal);
      if (!page) return; // キャンセル済み、または304
      yield page;
      cursor = page.next_cursor ?? undefined;
    } while (cursor);
  },
};

// --- オフライン/オンライン復帰時の後始末 ---
if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    document.dispatchEvent(new CustomEvent('xte:offline'));
  });
  window.addEventListener('online', () => {
    // オフライン中に貯まったin-flightの残骸を捨て、復帰後の取得を新規リクエストとしてやり直す。
    inFlight.clear();
    document.dispatchEvent(new CustomEvent('xte:online'));
  });
}

/** main.js の起動時に一度だけ呼び、nonceとsettings_versionをプリロードする。 */
export async function bootstrapApi() {
  return refreshConfig();
}

export function getCachedNonce() {
  return cachedNonce;
}

export function getCachedSettingsVersion() {
  return cachedSettingsVersion;
}

/**
 * settings_version の差分検知ポーリング（main.js、00文書 4.4節・34文書の決定）から呼ぶ。
 * 強制的に /config を再取得する。
 */
export async function forceRefreshConfig() {
  configPromise = null;
  return refreshConfig();
}
